import * as React from "react";
import { Outlet, createFileRoute, useNavigate } from "@tanstack/react-router";
import { Loader2Icon } from "lucide-react";

import { AppShell } from "@/components/app-shell";
import { ConsentGate } from "@/components/consent-gate";
import { OnboardingGate } from "@/components/onboarding-gate";
import { PasswordChangeGate } from "@/components/password-change-gate";
import { useSessionContext } from "@/lib/session";

export const Route = createFileRoute("/_app")({
  component: AppLayout,
});

/**
 * Everything behind this route requires a signed-in user.
 *
 * The actual protection lives in the backend, where every route is guarded by
 * default. The check here is only about user guidance, so nobody lands on empty
 * pages.
 */
function AppLayout() {
  const { user, isLoading } = useSessionContext();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!isLoading && !user) {
      void navigate({ to: "/login", replace: true });
    }
  }, [isLoading, user, navigate]);

  if (isLoading || !user) {
    return (
      <div className="text-muted-foreground flex min-h-dvh items-center justify-center gap-3 text-sm">
        <Loader2Icon className="size-4 animate-spin" />
        Wird geladen ...
      </div>
    );
  }

  // The one-time password must be changed before anything else is possible.
  if (user.mustChangePassword) return <PasswordChangeGate />;

  // Consent at first sign-in. Users should know what is recorded before the
  // first conversation is stored.
  if (!user.consentAt) return <ConsentGate />;

  // Onboarding: display name and coach. Comes after the consent because it
  // already stores something about the person. Advisors and admins only: the
  // admin trains too, the other roles never see a coach and would be stuck on
  // a screen with no purpose.
  if (
    (user.role === "advisor" || user.role === "platform") &&
    !user.coachStyleId
  )
    return <OnboardingGate />;

  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}
